import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ArrowRight } from "lucide-react";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { business } from "@/lib/business";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services", dropdown: "services" as const },
  { label: "Locations", href: "/locations", dropdown: "locations" as const },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

const Navbar = () => {
  const { openQuoteForm } = useQuoteForm();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openDropdown = (id: string) => {
    clearTimeout(hoverTimeout.current);
    setActiveDropdown(id);
  };
  const closeDropdown = () => {
    hoverTimeout.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  const mobileMenu = (
    <>
      <div
        className={`fixed inset-0 bg-[hsl(100_26%_4%/0.6)] backdrop-blur-sm z-[1098] md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <div
        className={`fixed top-0 left-0 h-full w-[19rem] max-w-[85vw] bg-card z-[1099] md:hidden transform transition-transform duration-300 ease-out shadow-2xl ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
            <img
              src="/logo.png"
              alt={`${business.name} logo`}
              width={167}
              height={95}
              className="h-11 w-auto"
            />
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-full text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col h-[calc(100%-64px)] overflow-y-auto scrollbar-hide">
          <ul className="flex flex-col px-4 py-3 gap-0.5">
            {navLinks.map((link) =>
              link.dropdown ? (
                <li key={link.href}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="flex-1 font-heading text-[17px] font-semibold text-foreground/85 hover:text-primary transition-colors py-3"
                    >
                      {link.label}
                    </Link>
                    <button
                      onClick={() =>
                        link.dropdown === "services"
                          ? setMobileServicesOpen((v) => !v)
                          : setMobileLocationsOpen((v) => !v)
                      }
                      className="p-2 text-foreground/70 hover:text-primary"
                      aria-label={`Toggle ${link.label} list`}
                      aria-expanded={
                        link.dropdown === "services" ? mobileServicesOpen : mobileLocationsOpen
                      }
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          (link.dropdown === "services" ? mobileServicesOpen : mobileLocationsOpen)
                            ? "rotate-180"
                            : ""
                        }`}
                      />
                    </button>
                  </div>
                  {link.dropdown === "services" && mobileServicesOpen && (
                    <ul className="pl-3 pb-2 border-l-2 border-primary/25 ml-1 space-y-0.5">
                      {services.map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`/services/${s.slug}`}
                            onClick={() => setOpen(false)}
                            className="block py-2 pl-3 text-sm text-foreground/75 hover:text-primary transition-colors"
                          >
                            {s.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {link.dropdown === "locations" && mobileLocationsOpen && (
                    <ul className="pl-3 pb-2 border-l-2 border-primary/25 ml-1 space-y-0.5">
                      {locations.map((loc) => (
                        <li key={loc.slug}>
                          <Link
                            to={`/locations/${loc.slug}`}
                            onClick={() => setOpen(false)}
                            className="block py-2 pl-3 text-sm text-foreground/75 hover:text-primary transition-colors"
                          >
                            {loc.name}, {loc.state}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="block py-3 font-heading text-[17px] font-semibold text-foreground/85 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              )
            )}
          </ul>

          <div className="mt-auto px-4 py-5 border-t border-border space-y-3">
            <Button
              className="w-full gap-2"
              onClick={() => {
                openQuoteForm();
                setOpen(false);
              }}
            >
              Get Free Estimate <ArrowRight className="h-4 w-4" />
            </Button>
            <Button asChild variant="outline" className="w-full gap-2">
              <a href={business.phoneHref}>
                <Phone className="h-4 w-4 text-primary" /> {business.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <nav className={`site-header ${scrolled ? "site-header--scrolled" : ""}`} aria-label="Primary">
        <div className="header-shell">
          <div className="header-inner container-max flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5">
            <Link to="/" className="header-logo flex items-center shrink-0" aria-label={`${business.name} — home`}>
              <img
                src="/logo.png"
                alt={`${business.name} logo`}
                width={167}
                height={95}
                className="h-11 lg:h-12 w-auto"
              />
            </Link>

            <ul className="hidden md:flex items-center gap-0.5 lg:gap-1">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <li
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => openDropdown(link.dropdown!)}
                    onMouseLeave={closeDropdown}
                  >
                    <Link
                      to={link.href}
                      data-active={location.pathname.startsWith(link.href)}
                      className="nav-pill font-heading text-[15px] font-medium text-foreground/85 hover:text-primary inline-flex items-center gap-1"
                    >
                      {link.label}
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${
                          activeDropdown === link.dropdown ? "rotate-180" : ""
                        }`}
                      />
                    </Link>

                    {activeDropdown === link.dropdown && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-[1010]">
                        {/* Compact two-column list — no images, no promos, no inner scrollbar */}
                        <div
                          className={`header-dropdown p-3 ${
                            link.dropdown === "services" ? "w-[34rem]" : "w-[26rem]"
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-x-2">
                            {link.dropdown === "services"
                              ? services.map((s) => (
                                  <Link
                                    key={s.slug}
                                    to={`/services/${s.slug}`}
                                    className="block rounded-lg px-3 py-2 text-[13.5px] font-medium text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    {s.title}
                                  </Link>
                                ))
                              : locations.map((loc) => (
                                  <Link
                                    key={loc.slug}
                                    to={`/locations/${loc.slug}`}
                                    className="block rounded-lg px-3 py-2 text-[13.5px] font-medium text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    {loc.name}, {loc.state}
                                  </Link>
                                ))}
                          </div>
                          <Link
                            to={link.href}
                            onClick={() => setActiveDropdown(null)}
                            className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-primary/8 px-3 py-2 text-[13px] font-bold text-primary hover:bg-primary/15 transition-colors"
                          >
                            View all {link.label.toLowerCase()}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </li>
                ) : (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      data-active={
                        link.href === "/"
                          ? location.pathname === "/"
                          : location.pathname.startsWith(link.href)
                      }
                      className="nav-pill font-heading text-[15px] font-medium text-foreground/85 hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                )
              )}
            </ul>

            <div className="hidden md:flex items-center gap-2 shrink-0">
              <a
                href={business.phoneHref}
                className="hidden xl:inline-flex items-center gap-2 rounded-full px-3 py-2 text-[14px] font-bold text-foreground hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4 text-primary" />
                {business.phone}
              </a>
              <Button size="sm" onClick={openQuoteForm} className="rounded-full px-5 btn-glow btn-shine">
                Get Free Estimate
              </Button>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="md:hidden text-foreground p-2 rounded-full hover:bg-primary/10 transition-colors"
              aria-label="Open menu"
              aria-expanded={open}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>
      {createPortal(mobileMenu, document.body)}
    </>
  );
};

export default Navbar;
